// The door code: a QR shown by a staff device at the door (staff app or kiosk). A parent's app
// scans it to drop off or pick up by themselves; the code proves a staff member was at the door
// at that moment. It rotates every 30 s and is signed by the staff device's key, so it can be
// generated offline and checked by the server against the enrolled device.
import { b64url, fromB64url, utf8 } from "./bytes.ts";
import { canonicalize } from "./canonical.ts";
import { signBytes, verifyBytes } from "./keys.ts";

/**
 * `staff_id` names the staff member at the door; it is null on the self-service door tablet's code,
 * which nobody staffs: those codes can only raise a request (pickup_request or dropoff_request, as
 * `purpose` says), never hand a child over.
 */
export interface DoorPayload {
  v: 1; kind: "door"; site_id: string; device_id: string; staff_id: string | null; window: number;
  /** What the code is for, when it says: the parent app opens drop-off or pickup to match. */
  purpose?: "pickup" | "dropoff";
}

export const DOOR_WINDOW_S = 30;
export const doorWindow = (unixSeconds: number) => Math.floor(unixSeconds / DOOR_WINDOW_S);

export async function issueDoorCode(deviceKey: CryptoKey, p: Omit<DoorPayload, "v" | "kind">): Promise<string> {
  const body = b64url(utf8(canonicalize({ v: 1, kind: "door", ...p } satisfies DoorPayload)));
  return `PMUD1.${body}.${await signBytes(deviceKey, utf8(body))}`;
}

/** Reads a door code without trusting it (the app shows the site and staff before sending). */
export function parseDoorCode(qr: string): { payload: DoorPayload; body: string; sig: string } | null {
  const parts = qr.trim().split(".");
  if (parts.length !== 3 || parts[0] !== "PMUD1") return null;
  try {
    const text = new TextDecoder().decode(fromB64url(parts[1]!));
    const payload = JSON.parse(text) as DoorPayload;
    if (canonicalize(payload) !== text || payload.v !== 1 || payload.kind !== "door") return null;
    return { payload, body: parts[1]!, sig: parts[2]! };
  } catch {
    return null;
  }
}

export type DoorCheck = { ok: true; payload: DoorPayload } | { ok: false; reason: string };

/** Server side: signature by the named device, right site, and fresh (current or two previous windows). */
export async function verifyDoorCode(qr: string, devicePubkey: string, siteId: string, nowSeconds: number, tolerance = 2): Promise<DoorCheck> {
  const parsed = parseDoorCode(qr);
  if (!parsed) return { ok: false, reason: "not a door code" };
  if (parsed.payload.site_id !== siteId) return { ok: false, reason: "door code is for another site" };
  if (!(await verifyBytes(devicePubkey, utf8(parsed.body), parsed.sig))) return { ok: false, reason: "door code signature invalid" };
  const w = doorWindow(nowSeconds);
  if (parsed.payload.window > w + 1 || parsed.payload.window < w - tolerance) return { ok: false, reason: "door code expired: scan it again" };
  return { ok: true, payload: parsed.payload };
}
